//@ts-nocheck
import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import formElementMixin from "@banno/jha-wc/src/js/mixins/form-element-mixin";
import { WorkflowTemplateGeneralViewMode } from "../../types/WorkflowTemplates/WorkflowTemplateTypeIndex";
import { WorkflowTemplateVariable } from "../../types/WorkflowTemplates/WorkflowTemplateVariable";
import { getRequest } from "../../utils/jha-fetch";
import { Apis } from "../../utils/api-constants";
import "./WorkflowTemplateNotificationView";

/**
 * Workflow Template Notification Tab
 *
 * This tab manages notification configurations for workflow templates:
 * - Success notifications (when workflow completes successfully)
 * - Failure notifications (when workflow fails)
 * - Email recipients
 * - Message templates
 *
 * Uses WorkflowTemplateNotificationView which provides:
 * - Inline editing with edit/delete action icons (like Variables grid)
 * - Add new notifications
 * - Full CRUD operations
 *
 * Validation Logic:
 * - Notifications are OPTIONAL - tab is valid by default with no notifications
 * - Tab is invalid if there's a row being edited (unsaved changes)
 * - Tab is valid when all notifications are saved
 */
@customElement("workflow-template-notification-tab")
export class WorkflowTemplateNotificationTab extends formElementMixin(LitElement) {
  static get is() {
    return "workflow-template-notification-tab";
  }

  @property({ type: String }) mode: WorkflowTemplateGeneralViewMode = "ADD";
  @property({ type: String }) workflowTemplateId: string = "";
  @property({ type: Boolean }) isReadOnly: boolean = false;
  @property({ type: Object }) templateData: any = null; // Passed from parent - no need to fetch
  @property({ type: Array }) variables: WorkflowTemplateVariable[] = [];

  // Notification list
  @state() notifications: any[] = [];

  // Loading state
  @state() apiLoading: boolean = false;

  // Tracks if there's an unsaved notification
  @state() hasUnsavedNotification: boolean = false;

  // Dirty state - true if there are unsaved changes
  @state() isDirty: boolean = false;

  // Flag to prevent marking dirty during initial load
  private isInitializing: boolean = true;

  // Original notification list for dirty detection
  private originalNotifications: any[] = [];

  static readonly styles = css`
    :host {
      display: block;
      padding: 20px;
    }

    h2 {
      margin-top: 0;
      margin-bottom: 20px;
      font-size: 24px;
      font-weight: 600;
    }

    @media (prefers-color-scheme: dark) {
      h2 {
        color: #a3d4ff;
      }
    }

    @media (prefers-color-scheme: light) {
      h2 {
        color: #06185f;
      }
    }

    .info-text {
      color: var(--jha-text-light);
      font-size: 14px;
      margin-bottom: 16px;
    }

    workflow-template-notification-view {
      display: block;
      margin-top: 16px;
    }
  `;

  async connectedCallback() {
    super.connectedCallback();

    // Load existing notifications if in EDIT or VIEW mode
    if (this.workflowTemplateId) {
      await this.loadNotifications();
    } else {
      // ADD mode - new template starts clean (isDirty = false)
      // Dirty state is only set when user actually changes something via markDirty()
      // Notifications are optional, so no notifications is valid
      this.isDirty = false;
    }

    this.validateAndEmit();
  }

  /**
   * Called after the first render - safe to start tracking dirty state
   * and emit initial validation to parent
   */
  firstUpdated() {
    setTimeout(() => {
      this.isInitializing = false;
      // Re-emit validation now that parent's event listeners are definitely attached
      this.validateAndEmit();
    }, 100);
  }

  /**
   * Load notifications for EDIT/VIEW mode
   * Uses templateData from parent if available (single fetch optimization)
   */
  async loadNotifications() {
    this.apiLoading = true;

    try {
      // Use templateData from parent if available, otherwise fetch (fallback for tests)
      const template = this.templateData || await getRequest(`${Apis.GET_WORKFLOW_TEMPLATE}/${this.workflowTemplateId}`);

      if (template && !template?.details?.length) {

        // Extract notifications from nested array (Note: property is "Notification" SINGULAR!)
        const notifications = template.notification || template.notifications || [];

        // Map to component format
        // C# API uses "to" and "template" field names
        this.notifications = notifications.map((n: any, index: number) => ({
          number: index + 1,
          messageType: n.messageType || "",
          notificationType: n.notificationType || "",
          to: n.to || "",
          subject: n.subject || "",
          template: n.template || "",
          isEditing: false,
          isNew: false,
        }));

        // Save original for dirty detection
        this.saveOriginalData();
        this.isDirty = false;
      }
    } catch (error) {
      console.error("Failed to load notifications:", error);
    } finally {
      this.apiLoading = false;
      this.validateAndEmit();
    }
  }

  /**
   * Save current notification list as original for dirty detection
   */
  saveOriginalData() {
    this.originalNotifications = JSON.parse(JSON.stringify(this.notifications));
  }

  /**
   * Mark form as dirty
   */
  markDirty() {
    if (!this.isInitializing) {
      this.isDirty = true;
    }
  }

  /**
   * Handle notification changes from the notification view component
   */
  onNotificationChange(e: CustomEvent) {
    const { notifications, hasUnsavedRow, isValid } = e.detail;

    this.notifications = notifications;
    this.hasUnsavedNotification = hasUnsavedRow;
    this.markDirty();
    this.validateAndEmit();
  }

  /**
   * Handle new variable added from template-string-input
   * Propagates the event to the parent component so it can add to the Settings tab
   */
  onVariableAdded(e: CustomEvent) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent("variable-added", {
        detail: e.detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Get variable options formatted for template-string-input (sorted alphabetically)
   */
  get variableOptions() {
    return (this.variables ?? [])
      .map((v) => ({
        name: v.name,
        value: v.name,
        description: v.description,
      }))
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }

  /**
   * Validate and emit validation state
   */
  validateAndEmit() {
    // Tab is always valid when optional and no editing rows
    // midEdit tracks when a row is being added/edited (blocks save)
    const midEdit = this.hasUnsavedNotification;
    this.emitValidation(true, midEdit);
  }

  /**
   * Emit validation-change event to parent
   * @param valid - Whether the tab is valid (notifications are optional, so usually true)
   * @param midEdit - Whether a notification row is being edited (blocks save)
   */
  emitValidation(valid: boolean, midEdit: boolean = false) {
    this.dispatchEvent(
      new CustomEvent("validation-change", {
        detail: {
          tabName: "notification",
          valid,
          dirty: this.isDirty,
          midEdit,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Get notification data for saving
   * Maps from component format to API format
   */
  getFormData() {
    return {
      notifications: this.notifications
        .filter((n) => !n.isEditing && !n.isNew) // Only include saved notifications
        .map((n) => ({
          messageType: n.messageType,
          notificationType: n.notificationType,
          to: n.to,
          subject: n.subject,
          template: n.template,
        })),
    };
  }

  render() {
    const isViewMode = this.mode === "VIEW";

    return html`
      <div>
        <h2>Notification Configuration</h2>
        <p class="info-text">
          Configure email notifications to be sent when workflows created from this template
          succeed or fail. Notifications are optional.
        </p>
      
        <workflow-template-notification-view data-auto-id="notificationView" .notifications=${this.notifications} .isReadOnly=${isViewMode || this.isReadOnly}
          .variables=${this.variables} @notification-change=${this.onNotificationChange}
          @variable-added=${this.onVariableAdded}>
        </workflow-template-notification-view>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "workflow-template-notification-tab": WorkflowTemplateNotificationTab;
  }
}
